import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import {
  Buildings, Users, Package, CreditCard, ChartBar, CaretRight,
  MagnifyingGlass, ArrowLeft, CheckCircle, Clock, XCircle, Crown, ChatCircleDots, EnvelopeSimple
} from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import api from '../lib/api';

const STATUS_COLORS = {
  active: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Active' },
  pending: { bg: 'bg-amber-500/10', text: 'text-amber-400', label: 'Pending' },
  suspended: { bg: 'bg-red-500/10', text: 'text-red-400', label: 'Suspended' },
  cancelled: { bg: 'bg-gray-500/10', text: 'text-gray-400', label: 'Cancelled' },
};

const PLAN_COLORS = {
  starter: { bg: 'bg-blue-500/10', text: 'text-blue-400' },
  pro: { bg: 'bg-[#FF9900]/10', text: 'text-[#FF9900]' },
  enterprise: { bg: 'bg-purple-500/10', text: 'text-purple-400' },
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, companiesRes, leadsRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/companies'),
        api.get('/chat/leads').catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setCompanies(companiesRes.data);
      setLeads(leadsRes.data || []);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadCompanyDetail = async (companyId) => {
    try {
      const res = await api.get(`/admin/companies/${companyId}`);
      setSelectedCompany(res.data);
    } catch {
      toast.error('Failed to load company details');
    }
  };

  const updateStatus = async (companyId, status) => {
    try {
      await api.put(`/admin/companies/${companyId}/status?status=${status}`);
      toast.success(`Company status updated to ${status}`);
      loadData();
      if (selectedCompany?.id === companyId) {
        loadCompanyDetail(companyId);
      }
    } catch {
      toast.error('Failed to update status');
    }
  };

  const filtered = companies.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.email.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-10 h-10 border-3 border-[#FF9900] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <Layout title="Admin">
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Platform Admin</h1>
          <p className="text-sm text-gray-400 mt-1">Manage companies, subscriptions, and platform health</p>
        </div>
      </div>

      {/* Platform Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="admin-stats-grid">
          {[
            { label: 'Companies', value: stats.companies?.total || 0, icon: Buildings, color: '#0080FF' },
            { label: 'Active', value: stats.companies?.active || 0, icon: CheckCircle, color: '#16A34A' },
            { label: 'Users', value: stats.users || 0, icon: Users, color: '#FF9900' },
            { label: 'Packages', value: stats.packages || 0, icon: Package, color: '#003166' },
            { label: 'Revenue', value: `$${(stats.revenue_usd || 0).toLocaleString()}`, icon: CreditCard, color: '#16A34A' },
            { label: 'Inquiries', value: stats.contact_submissions || 0, icon: ChartBar, color: '#8B5CF6' },
          ].map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-white/[0.03] border border-white/5 rounded-xl p-4"
            >
              <div className="flex items-center gap-2 mb-2">
                <s.icon size={16} style={{ color: s.color }} weight="duotone" />
                <span className="text-xs text-gray-500">{s.label}</span>
              </div>
              <p className="text-xl font-bold text-white">{s.value}</p>
            </motion.div>
          ))}
        </div>
      )}

      {/* Companies List */}
      <div className="bg-white/[0.03] border border-white/5 rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-white/5 flex items-center justify-between gap-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Buildings size={20} className="text-[#FF9900]" />
            Registered Companies
          </h2>
          <div className="relative w-64">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search companies..."
              className="pl-9 h-9 bg-white/5 border-white/10 text-white text-sm rounded-lg"
              data-testid="admin-search-input"
            />
          </div>
        </div>

        <div className="divide-y divide-white/5" data-testid="admin-companies-list">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No companies found</div>
          ) : (
            filtered.map((company) => {
              const statusCfg = STATUS_COLORS[company.subscription_status] || STATUS_COLORS.pending;
              const planCfg = PLAN_COLORS[company.plan] || PLAN_COLORS.starter;
              return (
                <div
                  key={company.id}
                  className="p-4 hover:bg-white/[0.02] transition-colors cursor-pointer flex items-center gap-4"
                  onClick={() => loadCompanyDetail(company.id)}
                  data-testid={`admin-company-${company.id}`}
                >
                  <div className="w-10 h-10 rounded-xl bg-[#003166]/30 flex items-center justify-center flex-shrink-0">
                    <Buildings size={20} className="text-[#0080FF]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-white truncate">{company.name}</p>
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${planCfg.bg} ${planCfg.text}`}>
                        {company.plan}
                      </span>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${statusCfg.bg} ${statusCfg.text}`}>
                        {statusCfg.label}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{company.email}</p>
                  </div>
                  <div className="hidden sm:flex items-center gap-6 text-xs text-gray-400">
                    <div className="text-center">
                      <p className="font-bold text-white">{company.customers}</p>
                      <p>Clients</p>
                    </div>
                    <div className="text-center">
                      <p className="font-bold text-white">{company.packages}</p>
                      <p>Packages</p>
                    </div>
                    <div className="text-center">
                      <p className="font-bold text-emerald-400">${company.revenue_usd}</p>
                      <p>Revenue</p>
                    </div>
                  </div>
                  <CaretRight size={16} className="text-gray-600 flex-shrink-0" />
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Chat Leads Section */}
      {leads.length > 0 && (
        <div className="bg-[#111827] border border-white/10 rounded-2xl p-6" data-testid="admin-leads-section">
          <div className="flex items-center gap-3 mb-4">
            <ChatCircleDots size={22} className="text-[#0077FF]" weight="fill" />
            <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Bebas Neue' }}>Chat Leads ({leads.length})</h2>
          </div>
          <div className="space-y-2">
            {leads.map((lead, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
                <div className="w-8 h-8 rounded-full bg-[#0077FF]/20 flex items-center justify-center">
                  <EnvelopeSimple size={14} className="text-[#0077FF]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{lead.name || 'Unknown'}</p>
                  <p className="text-gray-400 text-xs truncate">{lead.email}</p>
                </div>
                <p className="text-gray-500 text-[10px] flex-shrink-0">{lead.created_at ? new Date(lead.created_at).toLocaleDateString() : ''}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Company Detail Modal */}
      {selectedCompany && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelectedCompany(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="bg-[#111827] border border-white/10 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
            data-testid="admin-company-detail"
          >
            <div className="p-6 border-b border-white/5">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-bold text-white">{selectedCompany.name}</h3>
                  <p className="text-sm text-gray-400 mt-1">{selectedCompany.email} {selectedCompany.phone && `| ${selectedCompany.phone}`}</p>
                </div>
                <button onClick={() => setSelectedCompany(null)} className="p-2 hover:bg-white/5 rounded-lg text-gray-400">
                  <XCircle size={20} />
                </button>
              </div>
              <div className="flex items-center gap-3 mt-3">
                {(() => {
                  const sc = STATUS_COLORS[selectedCompany.subscription_status] || STATUS_COLORS.pending;
                  const pc = PLAN_COLORS[selectedCompany.plan] || PLAN_COLORS.starter;
                  return (
                    <>
                      <span className={`text-xs font-bold uppercase px-3 py-1 rounded-full ${pc.bg} ${pc.text}`}>
                        <Crown size={12} weight="fill" className="inline mr-1" />
                        {selectedCompany.plan} Plan
                      </span>
                      <span className={`text-xs font-medium px-3 py-1 rounded-full ${sc.bg} ${sc.text}`}>
                        {sc.label}
                      </span>
                    </>
                  );
                })()}
                <span className="text-xs text-gray-500">
                  Joined: {selectedCompany.created_at ? new Date(selectedCompany.created_at).toLocaleDateString() : 'N/A'}
                </span>
              </div>
            </div>

            {/* Stats */}
            <div className="p-6 grid grid-cols-4 gap-4">
              {[
                { label: 'Customers', value: selectedCompany.stats?.customers || 0, max: selectedCompany.max_clients },
                { label: 'Users', value: selectedCompany.users?.length || 0, max: selectedCompany.max_users },
                { label: 'Packages', value: selectedCompany.stats?.packages || 0 },
                { label: 'Revenue', value: `$${(selectedCompany.stats?.revenue_usd || 0).toLocaleString()}` },
              ].map((s, i) => (
                <div key={i} className="bg-white/[0.03] rounded-xl p-3 text-center">
                  <p className="text-lg font-bold text-white">{s.value}</p>
                  <p className="text-xs text-gray-500">{s.label}</p>
                  {s.max && <p className="text-[10px] text-gray-600 mt-1">max: {s.max}</p>}
                </div>
              ))}
            </div>

            {/* Users */}
            {selectedCompany.users?.length > 0 && (
              <div className="px-6 pb-4">
                <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-2">Team Members</h4>
                <div className="space-y-2">
                  {selectedCompany.users.map(u => (
                    <div key={u.id} className="flex items-center gap-3 bg-white/[0.03] rounded-lg p-2.5">
                      <div className="w-8 h-8 rounded-full bg-[#003166]/30 flex items-center justify-center">
                        <Users size={14} className="text-[#0080FF]" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-white">{u.name}</p>
                        <p className="text-xs text-gray-500">{u.email}</p>
                      </div>
                      <span className="text-[10px] font-medium text-gray-400 uppercase">{u.role}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="p-6 border-t border-white/5 flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => updateStatus(selectedCompany.id, 'active')}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                disabled={selectedCompany.subscription_status === 'active'}
              >
                Activate
              </Button>
              <Button
                size="sm"
                onClick={() => updateStatus(selectedCompany.id, 'suspended')}
                className="bg-amber-600 hover:bg-amber-700 text-white text-xs"
                disabled={selectedCompany.subscription_status === 'suspended'}
              >
                Suspend
              </Button>
              <Button
                size="sm"
                onClick={() => updateStatus(selectedCompany.id, 'cancelled')}
                className="bg-red-600 hover:bg-red-700 text-white text-xs"
                disabled={selectedCompany.subscription_status === 'cancelled'}
              >
                Cancel
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
    </Layout>
  );
}
